import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Eye, UserPlus, ChevronLeft, ChevronRight,
  Calendar, X, ChevronsLeft, ChevronsRight, Loader2, RefreshCw,
  ChevronUp, ChevronDown, ArrowUpDown, MessageSquare, Send, CheckSquare, Square, Edit2, Check
} from 'lucide-react';
import DateRangePicker from '../components/PlayerProfile/DateRangePicker';
import PlayerEntriesModal from '../components/PlayerProfile/PlayerEntriesModal';
import { playerProfileService } from '../apis/playerProfileService';
import { Button } from '../components/ui/button';
import toast from 'react-hot-toast';
import { getBulkSmsPayload, toastBulkSmsResult } from '../utils/smsSendResult';
import { useSelector, useDispatch } from "react-redux";
import {
  GET_PLAYER_PROFILES,
  SET_ALL_PLAYERS,
  SET_LOADING,
  SET_STATS_LOADING,
  SET_ERROR
} from "../Store/actions/playerprofile";

// Default SMS Template
const DEFAULT_SMS_TEMPLATE = `EPL: Cards, cocktails, food, and a great crowd — starting today at 6 PM.

Join us for our 10k Monthly Classic tonight!

View details: https://www.elitepokerlounge.com/assets/uploads/events_tournaments/695bb883dfea3.webp

Msg & data rates may apply.

Reply STOP to opt out, HELP for help.

Terms: https://elitepokerlounge.com/terms | Privacy: https://elitepokerlounge.com/privacy`;

const toSearchText = (value) => String(value ?? '').toLowerCase().trim();
const toDigits = (value) => String(value ?? '').replace(/\D/g, '');

const unwrapPlayerList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.players)) return payload.players;
  return [];
};

const matchesPlayerSearch = (player, rawTerm) => {
  const term = toSearchText(rawTerm);
  if (!term) return true;

  const name = toSearchText(player?.player_name);
  const number = toSearchText(player?.player_number);
  const email = toSearchText(player?.email);
  const phone = toSearchText(player?.phone);
  const flippedName = name.includes(',')
    ? name.split(',').map((part) => part.trim()).filter(Boolean).reverse().join(' ')
    : name.split(/\s+/).filter(Boolean).reverse().join(' ');
  const haystack = [name, flippedName, number, email, phone].join(' ');

  if (haystack.includes(term)) return true;

  const termDigits = toDigits(term);
  const phoneDigits = toDigits(phone);
  const numberDigits = toDigits(number);
  if (termDigits.length >= 4 && (phoneDigits.includes(termDigits) || numberDigits.includes(termDigits))) {
    return true;
  }

  const tokens = term.split(/[,\s]+/).filter(Boolean);
  if (tokens.length > 1) {
    return tokens.every((token) => {
      if (haystack.includes(token)) return true;
      const tokenDigits = toDigits(token);
      return tokenDigits.length >= 3 && (
        phoneDigits.includes(tokenDigits) || numberDigits.includes(tokenDigits)
      );
    });
  }

  return false;
};

// Enhanced Loading Component
const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-10 w-10'
  };

  return (
    <Loader2 className={`animate-spin text-blue-600 ${sizes[size]} ${className}`} />
  );
};

// Table Row Skeleton Loader
const TableRowSkeleton = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center">
        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full"></div>
        <div className="ml-4">
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-16"></div>
        </div>
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-20"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-20"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-200 rounded w-20"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-right">
      <div className="flex items-center justify-end space-x-2">
        <div className="h-8 w-8 bg-gray-200 rounded"></div>
      </div>
    </td>
  </tr>
);

// Stats Card Skeleton
const StatsCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow p-4 border border-gray-200 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
    <div className="h-8 bg-gray-200 rounded w-16"></div>
  </div>
);

// Sortable Header Component
const SortableHeader = ({ children, sortKey, currentSort, onSort, className = "" }) => {
  const getSortIcon = () => {
    if (currentSort.key !== sortKey) {
      return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400" />;
    }

    return currentSort.direction === 'asc'
      ? <ChevronUp className="ml-1 h-4 w-4 text-blue-600" />
      : <ChevronDown className="ml-1 h-4 w-4 text-blue-600" />;
  };

  const handleClick = () => {
    let direction = 'asc';
    if (currentSort.key === sortKey && currentSort.direction === 'asc') {
      direction = 'desc';
    }
    onSort(sortKey, direction);
  };

  return (
    <th
      className={`px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none ${className}`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <span>{children}</span>
        {getSortIcon()}
      </div>
    </th>
  );
};

const PlayerProfileModule = () => {
  const dispatch = useDispatch();
  const {
    allPlayers,
    loading: reduxLoading,
    statsLoading: reduxStatsLoading,
    error: reduxError
  } = useSelector((state) => state.playerprofile);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [dateRangeLabel, setDateRangeLabel] = useState('All Time');
  const [sortConfig, setSortConfig] = useState({ key: 'player_name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showPlayerImageModal, setShowPlayerImageModal] = useState(false);
  const [selectedPlayerImage, setSelectedPlayerImage] = useState('');
  const [selectedPlayerName, setSelectedPlayerName] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  const [stats, setStats] = useState(null);
  const [showBulkMessagingModal, setShowBulkMessagingModal] = useState(false);
  const [bulkMessageText, setBulkMessageText] = useState(DEFAULT_SMS_TEMPLATE);
  const [isBulkMessageEditable, setIsBulkMessageEditable] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [sendingBulkMessages, setSendingBulkMessages] = useState(false);
  const [bulkModalSearchTerm, setBulkModalSearchTerm] = useState('');
  const [debouncedBulkSearchTerm, setDebouncedBulkSearchTerm] = useState('');
  const [bulkModalCurrentPage, setBulkModalCurrentPage] = useState(1);
  const [bulkModalItemsPerPage, setBulkModalItemsPerPage] = useState(50);
  const [showSendConfirmationModal, setShowSendConfirmationModal] = useState(false);
  const [manualPhoneNumbers, setManualPhoneNumbers] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualPhoneInput, setManualPhoneInput] = useState('');
  const [phoneInputError, setPhoneInputError] = useState('');
  const [bulkPhoneInput, setBulkPhoneInput] = useState('');
  const [bulkPhoneError, setBulkPhoneError] = useState('');
  const [sendingToActivePlayers, setSendingToActivePlayers] = useState(false);
  const [showActivePlayersSendConfirmationModal, setShowActivePlayersSendConfirmationModal] = useState(false);
  const [activeSendResult, setActiveSendResult] = useState(null); // { successCount, failCount, total, failed, sendDate }
  const [showActiveSendResultModal, setShowActiveSendResultModal] = useState(false);
  const [activeSendResultLogsSummary, setActiveSendResultLogsSummary] = useState(null); // { sent, failed } from SMS logs for send date
  const [activeSendResultLogsLoading, setActiveSendResultLogsLoading] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const [activePlayers, setActivePlayers] = useState([]);
  const [activePlayersLoading, setActivePlayersLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: SET_LOADING, payload: true });
        dispatch({ type: SET_STATS_LOADING, payload: true });

        // Fetch all players without pagination
        const playersResponse = await playerProfileService.getDistinctPlayers(
          1,
          10000, // Large number to get all players
          ""
        );

        dispatch({ type: SET_ALL_PLAYERS, payload: unwrapPlayerList(playersResponse) });

      } catch (error) {
        console.error('Error fetching data:', error);
        dispatch({ type: SET_ERROR, payload: 'Failed to fetch data' });
      } finally {
        dispatch({ type: SET_LOADING, payload: false });
        dispatch({ type: SET_STATS_LOADING, payload: false });
      }
    };

    fetchData();
  }, [dispatch]);

  // Fetch stats with loading state
  const fetchStats = useCallback(async () => {
    dispatch({ type: SET_STATS_LOADING, payload: true });
    try {
      const response = await playerProfileService.getProfileStats();
      if (response && response.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      dispatch({ type: SET_STATS_LOADING, payload: false });
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch({ type: GET_PLAYER_PROFILES });
    fetchStats();
  }, [dispatch, fetchStats]);

  useEffect(() => {
    if (searchTerm.trim() || dateRange) {
      setIsFiltering(true);
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm, dateRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateRange, activeOnly]);

  // Debounce bulk modal search for performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBulkSearchTerm(bulkModalSearchTerm);
      setBulkModalCurrentPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [bulkModalSearchTerm]);


  const filteredAndSortedPlayers = useMemo(() => {
    const baseList = unwrapPlayerList(activeOnly ? activePlayers : allPlayers);
    if (baseList.length === 0) return [];

    let result = [...baseList];

    // Apply search filter (name, LAST/FIRST, player #, email, phone digits)
    if (searchTerm.trim()) {
      result = result.filter((player) => matchesPlayerSearch(player, searchTerm));
    }

    // Apply date filter
    if (dateRange) {
      result = result.filter(player => {
        const openDate = new Date(player.open_date);
        const closeDate = new Date(player.closed_date);
        return (
          closeDate >= dateRange.startDate && openDate <= dateRange.endDate
        );
      });
    }
    
    // Apply sorting
    return result.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [allPlayers, activeOnly, activePlayers, searchTerm, dateRange, sortConfig]);

  // Paginate results
  const paginatedPlayers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedPlayers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedPlayers, currentPage, itemsPerPage]);

  const totalPlayers = filteredAndSortedPlayers.length;
  const totalPages = Math.ceil(totalPlayers / itemsPerPage);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  // Get player status badge
  const getStatusBadge = (open_date) => {
    return open_date ? (
      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
        Active
      </span>
    ) : (
      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
        Closed
      </span>
    );
  };

  // Get player initials for avatar
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Handle viewing player details
  const handleViewPlayer = (player) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  // Handle date range selection
  const handleDateRangeApply = (start, end, label) => {
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(end);
    endDate.setHours(23, 59, 59, 999);

    setDateRange({ startDate, endDate });
    setDateRangeLabel(label);
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  const clearDateFilter = () => {
    setDateRange(null);
    setDateRangeLabel('All Time');
    setCurrentPage(1);
  };

  const handleToggleActivePlayersView = async () => {
    // Toggle off
    if (activeOnly) {
      setActiveOnly(false);
      setCurrentPage(1);
      return;
    }

    setActivePlayersLoading(true);
    try {
      const response = await playerProfileService.getActivePlayers();
      if (!response?.success) {
        toast.error(response?.error || 'Failed to load active players');
        return;
      }

      const players = response?.data?.data?.players || [];
      setActivePlayers(players);
      setActiveOnly(true);
      setCurrentPage(1);
      toast.success(`Loaded ${players.length} active player(s)`);
    } catch (error) {
      console.error('Error loading active players:', error);
      toast.error('Failed to load active players');
    } finally {
      setActivePlayersLoading(false);
    }
  };

  // Format date for display
  const formatDateDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Pagination calculations
  const getDisplayRange = () => {
    if (totalPlayers === 0) return { start: 0, end: 0 };
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalPlayers);
    return { start, end };
  };

  const { start: displayStart, end: displayEnd } = getDisplayRange();

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Refresh data
  const handleRefresh = () => {
    setSearchTerm('');
    setDateRange(null);
    setDateRangeLabel('All Time');
    setSortConfig({ key: 'player_name', direction: 'asc' });
    setCurrentPage(1);

    // Re-fetch data
    dispatch({ type: SET_LOADING, payload: true });
    playerProfileService.getDistinctPlayers(1, 10000, "")
      .then(response => {
        dispatch({ type: SET_ALL_PLAYERS, payload: unwrapPlayerList(response) });
      })
      .catch(error => {
        console.error('Refresh failed:', error);
        dispatch({ type: SET_ERROR, payload: 'Refresh failed' });
      })
      .finally(() => {
        dispatch({ type: SET_LOADING, payload: false });
      });
  };

  // Helper function to get unique player ID
  const getPlayerId = (player) => {
    if (player.id) return player.id.toString();
    // Create a unique ID from player_number and player_name
    return `${player.player_number || 'no-number'}-${player.player_name || 'no-name'}-${player.phone || 'no-phone'}`;
  };

  // Bulk Messaging Functions
  const handleTogglePlayerSelection = (playerId) => {
    const newSelected = new Set(selectedPlayers);
    if (newSelected.has(playerId)) {
      newSelected.delete(playerId);
    } else {
      newSelected.add(playerId);
    }
    setSelectedPlayers(newSelected);
  };

  const handleSelectAll = () => {
    // Get only the currently visible/paginated players (not all filtered)
    const visiblePlayers = paginatedPlayersForModal;
    const visiblePlayerIds = new Set(visiblePlayers.map(p => getPlayerId(p)));
    
    // Check if all visible players are already selected
    const allVisibleSelected = visiblePlayers.length > 0 && 
      visiblePlayers.every(p => selectedPlayers.has(getPlayerId(p)));
    
    if (allVisibleSelected) {
      // Deselect only the visible players, keep others selected
      const newSelected = new Set(selectedPlayers);
      visiblePlayerIds.forEach(id => newSelected.delete(id));
      setSelectedPlayers(newSelected);
    } else {
      // Add visible players to existing selections (merge, don't replace)
      const newSelected = new Set(selectedPlayers);
      visiblePlayerIds.forEach(id => newSelected.add(id));
      setSelectedPlayers(newSelected);
    }
  };

  // Get filtered players for modal (with phone numbers and search filter)
  // Uses the same search logic as the main page for consistency
  // Optimized with memoization and debounced search
  const getFilteredPlayersForModal = useCallback(() => {
    if (!allPlayers || allPlayers.length === 0) return [];

    // Start with all players that have phone numbers and valid names
    let filtered = allPlayers.filter(p => {
      // Must have phone number
      if (!p.phone || !p.phone.trim()) return false;
      // Must have valid player name
      if (!p.player_name) return false;
      const trimmedName = p.player_name.trim();
      if (!trimmedName || trimmedName === ',' || trimmedName.length < 2) return false;
      return true;
    });

    // Remove duplicates based on player_number and player_name combination
    const seen = new Set();
    filtered = filtered.filter(player => {
      const playerKey = `${player.player_number || 'no-number'}-${player.player_name?.trim() || 'no-name'}`;
      if (seen.has(playerKey)) return false;
      seen.add(playerKey);
      return true;
    });

    // Apply search filter - EXACT same logic as main page search
    // Use debounced search term for performance
    if (debouncedBulkSearchTerm.trim()) {
      filtered = filtered.filter((player) =>
        matchesPlayerSearch(player, debouncedBulkSearchTerm)
      );
    }

    // Apply date filter if active (same as main page)
    if (dateRange) {
      filtered = filtered.filter(player => {
        const openDate = new Date(player.open_date);
        const closeDate = new Date(player.closed_date);
        return (
          closeDate >= dateRange.startDate && openDate <= dateRange.endDate
        );
      });
    }

    // Apply sorting (same as main page)
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [allPlayers, debouncedBulkSearchTerm, dateRange, sortConfig]);

  // Validate USA phone number format
  const validateUSAPhoneNumber = useCallback((phone) => {
    if (!phone || !phone.trim()) return { valid: false, error: 'Phone number is required' };
    
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[\s\-().]/g, '');
    
    // Check if it's empty
    if (!cleaned) return { valid: false, error: 'Phone number is required' };
    
    // Check if it starts with +1
    if (cleaned.startsWith('+1')) {
      const digits = cleaned.substring(2);
      if (digits.length === 10 && /^\d{10}$/.test(digits)) {
        return { valid: true, formatted: `+1${digits}` };
      }
      return { valid: false, error: 'Invalid format. Use +1 followed by 10 digits' };
    }
    
    // Check if it starts with 1 (11 digits total)
    if (cleaned.startsWith('1') && cleaned.length === 11 && /^1\d{10}$/.test(cleaned)) {
      return { valid: true, formatted: `+1${cleaned.substring(1)}` };
    }
    
    // Check if it's 10 digits (without country code)
    if (cleaned.length === 10 && /^\d{10}$/.test(cleaned)) {
      return { valid: true, formatted: `+1${cleaned}` };
    }
    
    // Check if it has + but wrong format
    if (cleaned.includes('+') && !cleaned.startsWith('+1')) {
      return { valid: false, error: 'USA numbers must start with +1' };
    }
    
    return { valid: false, error: 'Invalid format. Use +1XXXXXXXXXX or XXXXXXXXXX (10 digits)' };
  }, []);

  // Format phone number as user types (USA format)
  const formatPhoneInput = (value) => {
    // If empty, return empty
    if (!value || value.trim() === '') return '';
    
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // If no digits, return empty (allows full deletion)
    if (digits.length === 0) return '';
    
    // Limit to 11 digits (1 + 10)
    const limited = digits.slice(0, 11);
    
    // Format based on length
    if (limited.length === 0) return '';
    
    // If starts with 1 and has 11 digits, remove the leading 1
    if (limited.startsWith('1') && limited.length === 11) {
      const formatted = `+1${limited.substring(1)}`;
      // If result is just "+1" with no actual digits, return empty
      if (formatted === '+1') return '';
      return formatted;
    }
    
    // Otherwise, add +1 prefix
    const formatted = `+1${limited}`;
    // If result is just "+1" with no actual digits, return empty
    if (formatted === '+1') return '';
    return formatted;
  };

  // Handle adding phone number to list
  const handleAddManualPhone = () => {
    const validation = validateUSAPhoneNumber(manualPhoneInput);
    
    if (!validation.valid) {
      setPhoneInputError(validation.error);
      return;
    }
    
    setPhoneInputError('');
    
    // Add to list (comma-separated or newline)
    if (manualPhoneNumbers.trim()) {
      setManualPhoneNumbers(prev => `${prev}\n${validation.formatted}`);
    } else {
      setManualPhoneNumbers(validation.formatted);
    }
    
    // Clear input
    setManualPhoneInput('');
  };

  // Normalize phone number to +1XXXXXXXXXX format
  // This ensures all phone numbers have the +1 prefix before sending SMS
  const normalizePhoneForSMS = useCallback((phone) => {
    if (!phone || !phone.trim()) return null;
    
    const cleaned = phone.trim();
    const digits = cleaned.replace(/\D/g, '');
    
    // If already in +1XXXXXXXXXX format, return as is
    if (cleaned.startsWith('+1') && digits.length === 12) {
      return cleaned;
    }
    
    // Handle different formats
    if (digits.length === 10) {
      // 10 digits (XXXXXXXXXX): add +1 prefix
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      // 11 digits starting with 1 (1XXXXXXXXXX): add + prefix
      return `+${digits}`;
    } else if (cleaned.startsWith('+') && digits.length === 11) {
      // + followed by 11 digits
      if (digits.startsWith('1')) {
        return `+${digits}`;
      }
    }
    
    // If we can't normalize, return null
    return null;
  }, []);

  // Parse manual phone numbers from stored string
  const parseManualPhoneNumbers = useCallback((phoneString) => {
    if (!phoneString.trim()) return [];
    
    // Split by comma, newline, or semicolon
    const numbers = phoneString
      .split(/[,\n;]/)
      .map(num => num.trim())
      .filter(num => num.length > 0);
    
    // Validate and normalize each number
    return numbers
      .map(num => {
        const validation = validateUSAPhoneNumber(num);
        return validation.valid ? validation.formatted : null;
      })
      .filter(num => num !== null);
  }, [validateUSAPhoneNumber]);

  // Handle bulk phone number entry - parse, validate, normalize with +1, and add all
  const handleBulkPhoneEntry = useCallback(() => {
    if (!bulkPhoneInput.trim()) {
      setBulkPhoneError('Please enter at least one phone number');
      return;
    }

    // Split by various delimiters: comma, newline, semicolon, space, tab
    const rawNumbers = bulkPhoneInput
      .split(/[,\n; \t]+/)
      .map(num => num.trim())
      .filter(num => num.length > 0);

    if (rawNumbers.length === 0) {
      setBulkPhoneError('No valid phone numbers found');
      return;
    }

    // Validate and normalize each number
    const validNumbers = [];
    const invalidNumbers = [];

    rawNumbers.forEach((num, index) => {
      // Use normalizePhoneForSMS to ensure +1 prefix
      const normalized = normalizePhoneForSMS(num);
      
      if (normalized) {
        // Check if already in the list
        const existingNumbers = parseManualPhoneNumbers(manualPhoneNumbers);
        if (!existingNumbers.includes(normalized)) {
          validNumbers.push(normalized);
        }
      } else {
        invalidNumbers.push({ number: num, index: index + 1 });
      }
    });

    if (validNumbers.length === 0) {
      setBulkPhoneError(
        invalidNumbers.length > 0
          ? `No valid phone numbers found. ${invalidNumbers.length} invalid number(s).`
          : 'All numbers are already in the list or invalid.'
      );
      return;
    }

    // Add all valid numbers to the list
    const newNumbers = validNumbers.join('\n');
    if (manualPhoneNumbers.trim()) {
      setManualPhoneNumbers(prev => `${prev}\n${newNumbers}`);
    } else {
      setManualPhoneNumbers(newNumbers);
    }

    // Show success message
    const successMsg = `Successfully added ${validNumbers.length} number(s)`;
    const errorMsg = invalidNumbers.length > 0 
      ? ` (${invalidNumbers.length} invalid number(s) skipped)` 
      : '';
    
    toast.success(successMsg + errorMsg);
    
    // Clear bulk input and errors
    setBulkPhoneInput('');
    setBulkPhoneError('');

    // Show invalid numbers if any
    if (invalidNumbers.length > 0) {
      console.warn('Invalid phone numbers:', invalidNumbers);
    }
  }, [bulkPhoneInput, manualPhoneNumbers, normalizePhoneForSMS, parseManualPhoneNumbers]);

  const handleBulkSendMessages = () => {
    if (!bulkMessageText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    const manualPhones = parseManualPhoneNumbers(manualPhoneNumbers);
    
    if (selectedPlayers.size === 0 && manualPhones.length === 0) {
      toast.error('Please select at least one player or enter phone numbers');
      return;
    }

    // Show confirmation modal with selected players and manual numbers
    setShowSendConfirmationModal(true);
  };

  const confirmAndSendMessages = async () => {
    // Get selected players with phone numbers
    const playersToSend = allPlayers.filter(player => {
      const playerId = getPlayerId(player);
      return selectedPlayers.has(playerId) && player.phone && player.phone.trim() !== '';
    });

    // Get manual phone numbers
    const manualPhones = parseManualPhoneNumbers(manualPhoneNumbers);

    if (playersToSend.length === 0 && manualPhones.length === 0) {
      toast.error('No valid recipients selected');
      setShowSendConfirmationModal(false);
      return;
    }

    setShowSendConfirmationModal(false);
    setSendingBulkMessages(true);

    try {
      // Collect recipients with metadata for tracking
      const recipients = [
        ...playersToSend
          .map(player => {
            const normalized = normalizePhoneForSMS(player.phone);
            if (!normalized) return null;
            return {
              player_id: player.id || null,
              player_number: player.player_number || null,
              player_name: player.player_name || null,
              phone: normalized
            };
          })
          .filter(Boolean),
        ...manualPhones.map(p => ({ phone: p }))
      ];

      // Filter out any null values (invalid phone numbers)
      if (recipients.length === 0) {
        toast.error('No valid phone numbers found. Please ensure phone numbers are in correct format (+1XXXXXXXXXX or XXXXXXXXXX)');
        setSendingBulkMessages(false);
        return;
      }

      // Send bulk SMS using the bulk endpoint
      const response = await playerProfileService.sendBulkSMS(
        recipients,
        bulkMessageText
      );

      const payload = getBulkSmsPayload(response);
      toastBulkSmsResult(toast, payload);

      if (payload.ok || payload.successCount > 0 || payload.skipCount > 0) {
        if (payload.failCount === 0 && payload.skipCount === 0) {
          setIsBulkMessageEditable(false);
          setSelectedPlayers(new Set());
          setManualPhoneNumbers('');
          setManualPhoneInput('');
          setPhoneInputError('');
          setBulkPhoneInput('');
          setBulkPhoneError('');
          setShowManualEntry(false);
          setShowBulkMessagingModal(false);
        }
      }
    } catch (error) {
      console.error('Error in bulk messaging:', error);
      const errorMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'An error occurred while sending messages';
      toast.error(errorMsg);
    } finally {
      setSendingBulkMessages(false);
    }
  };

  // Handler to send SMS to all active players
  const handleSendToActivePlayers = () => {
    if (!bulkMessageText.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setShowActivePlayersSendConfirmationModal(true);
  };

  const confirmAndSendToActivePlayers = async () => {
    setShowActivePlayersSendConfirmationModal(false);
    setSendingToActivePlayers(true);

    try {
      // Get active players' phone numbers
      const response = await playerProfileService.getActivePlayersPhones();
      
      if (!response.success || !response.data) {
        toast.error(response.error || 'Failed to get active players');
        setSendingToActivePlayers(false);
        return;
      }

      const players = response.data.data?.players || [];
      const phoneNumbers = response.data.data?.phoneNumbers || [];
   
      if ((players && players.length === 0) && phoneNumbers.length === 0) {
        toast.error('No active players with phone numbers found');
        setSendingToActivePlayers(false);
        return;
      }

      // Send bulk SMS to all active players
      const recipients = (players && players.length > 0)
        ? players
            .map(p => {
              const normalized = normalizePhoneForSMS(p.phone);
              if (!normalized) return null;
              return {
                player_id: p.player_id || null,
                player_number: p.player_number || null,
                player_name: p.player_name || null,
                phone: normalized
              };
            })
            .filter(Boolean)
        : phoneNumbers;

      const sendResponse = await playerProfileService.sendBulkSMS(recipients, bulkMessageText);
      const payload = getBulkSmsPayload(sendResponse);
      const sendDate = new Date();

      setActiveSendResult({
        successCount: payload.successCount,
        skipCount: payload.skipCount,
        failCount: payload.failCount,
        total: payload.total,
        skipped: payload.skipped,
        failed: payload.failed,
        sendDate
      });
      setShowActiveSendResultModal(true);
      setActiveSendResultLogsSummary(null);
      setActiveSendResultLogsLoading(false);
      toastBulkSmsResult(toast, payload);
    } catch (error) {
      console.error('Error sending to active players:', error);
      const errorMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'An error occurred while sending messages';
      toast.error(errorMsg);
    } finally {
      setSendingToActivePlayers(false);
    }
  };

  // Fetch SMS logs for the send date to show date-wise counts (re-derived from logs)
  const fetchActiveSendResultLogsSummary = useCallback(async () => {
    if (!activeSendResult?.sendDate) return;
    setActiveSendResultLogsLoading(true);
    try {
      const d = new Date(activeSendResult.sendDate);
      const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00.000`;
      const to = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T23:59:59.999`;
      const response = await playerProfileService.getSmsLogs({ page: 1, limit: 5000, export: '1', from, to });
      const data = response?.data?.data || {};
      const rows = data.rows || [];
      const sent = rows.filter((r) => r.success).length;
      const failed = rows.filter((r) => !r.success).length;
      setActiveSendResultLogsSummary({ sent, failed, total: rows.length });
    } catch (e) {
      console.error('Failed to fetch SMS logs for date:', e);
      setActiveSendResultLogsSummary({ error: true });
    } finally {
      setActiveSendResultLogsLoading(false);
    }
  }, [activeSendResult?.sendDate]);

  useEffect(() => {
    if (showActiveSendResultModal && activeSendResult?.sendDate && activeSendResultLogsSummary === null && !activeSendResultLogsLoading) {
      fetchActiveSendResultLogsSummary();
    }
  }, [showActiveSendResultModal, activeSendResult?.sendDate, activeSendResultLogsSummary, activeSendResultLogsLoading, fetchActiveSendResultLogsSummary]);

  // Get filtered players with phone numbers for selection (includes search filter)
  const playersWithPhone = useMemo(() => {
    return getFilteredPlayersForModal();
  }, [getFilteredPlayersForModal]);

  // Paginated players for modal (only render visible items)
  const paginatedPlayersForModal = useMemo(() => {
    const startIndex = (bulkModalCurrentPage - 1) * bulkModalItemsPerPage;
    return playersWithPhone.slice(startIndex, startIndex + bulkModalItemsPerPage);
  }, [playersWithPhone, bulkModalCurrentPage, bulkModalItemsPerPage]);

  const totalModalPages = Math.ceil(playersWithPhone.length / bulkModalItemsPerPage);

  // Get selected player names for display
  const selectedPlayerNames = useMemo(() => {
    if (selectedPlayers.size === 0) return [];
    
    return allPlayers
      .filter(player => {
        const playerId = getPlayerId(player);
        return selectedPlayers.has(playerId) && player.phone && player.phone.trim() !== '';
      })
      .map(player => ({
        name: player.player_name || 'N/A',
        phone: player.phone,
        playerNumber: player.player_number || 'N/A'
      }));
  }, [selectedPlayers, allPlayers]);

  // Get manual phone numbers for display
  const manualPhoneNumbersList = useMemo(() => {
    return parseManualPhoneNumbers(manualPhoneNumbers);
  }, [manualPhoneNumbers, parseManualPhoneNumbers]);
  // Render pagination buttons
  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;

    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPlayers, startPage + maxVisiblePages - 1);

    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add ellipsis and first page if needed
    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="h-8 w-8 border rounded text-sm font-medium transition-colors bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
        >
          1
        </button>
      );

      if (startPage > 2) {
        buttons.push(
          <span key="start-ellipsis" className="px-2 py-1 text-gray-500">...</span>
        );
      }
    }

    // Add visible page buttons
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`h-8 w-8 border rounded text-sm font-medium transition-colors ${currentPage === i
            ? 'bg-blue-50 border-blue-500 text-blue-600'
            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
            }`}
        >
          {i}
        </button>
      );
    }

    // Add ellipsis and last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="end-ellipsis" className="px-2 py-1 text-gray-500">...</span>
        );
      }

      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="h-8 w-8 border rounded text-sm font-medium transition-colors bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="bg-gray-50 p-6">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Player Profiles</h1>
              <p className="text-gray-600 mt-2 text-sm">
                Manage and view all player profiles
              </p>
            </div>
    
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 justify-center text-center">
          {reduxStatsLoading ? (
            Array(3).fill(0).map((_, index) => (
              <StatsCardSkeleton key={index} />
            ))
          ) : stats ? (
            <>
              <div className="bg-white rounded-lg shadow p-4 border border-blue-200">
                <div className="text-sm font-medium text-blue-600">Total Players</div>
                <div className="text-2xl font-bold text-blue-800">{stats?.total_profiles || 0}</div>
              </div>
              <button
                type="button"
                onClick={handleToggleActivePlayersView}
                disabled={activePlayersLoading}
                className={`bg-white rounded-lg shadow p-4 border text-center transition-colors ${
                  activeOnly
                    ? 'border-green-500 bg-green-50'
                    : 'border-green-200 hover:bg-green-50'
                } ${activePlayersLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                title={activeOnly ? 'Click to show all players' : 'Click to view active players'}
              >
                <div className="text-sm font-medium text-green-600">
                  Active Players {activeOnly ? '(showing)' : '(view)'}
                </div>
                <div className="text-2xl font-bold text-green-800">{stats?.active_profiles || 0}</div>
              </button>
              <div className="bg-white rounded-lg shadow p-4 border border-purple-200">
                <div className="text-sm font-medium text-purple-600">Avg Session Time</div>
                <div className="text-2xl font-bold text-purple-800">{Math.round(stats?.avg_session_mins || 0)} min</div>
              </div>
            </>
          ) : (
            <div className="col-span-full text-center py-4 text-gray-500">
              Unable to load statistics
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search name, player #, or phone..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {activeOnly && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Showing active players
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveOnly(false);
                      setCurrentPage(1);
                    }}
                    className="text-xs font-medium text-green-700 hover:text-green-900 underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={async () => {
                  setShowBulkMessagingModal(true);
                  setIsBulkMessageEditable(false);
                  try {
                    const res = await playerProfileService.getSmsTemplate('default');
                    const body = res?.data?.data?.body ?? res?.data?.body;
                    if (res?.success && body != null) {
                      setBulkMessageText(body);
                    } else {
                      setBulkMessageText(DEFAULT_SMS_TEMPLATE);
                    }
                  } catch {
                    setBulkMessageText(DEFAULT_SMS_TEMPLATE);
                  }
                }}
                className="flex items-center px-4 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <MessageSquare className="mr-2" size={16} />
                Messaging
              </button>

              <button
                onClick={() => setShowDatePicker(true)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Calendar className="mr-2" size={16} />
                {dateRange ? (
                  <span>
                    {formatDateDisplay(dateRange.startDate)} - {formatDateDisplay(dateRange.endDate)}
                  </span>
                ) : (
                  <span>{dateRangeLabel}</span>
                )}
              </button>

              {dateRange && (
                <button
                  onClick={clearDateFilter}
                  className="flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800"
                >
                  <X size={16} />
                  <span className="ml-1">Clear</span>
                </button>
              )}

              <button
                onClick={handleRefresh}
                disabled={reduxLoading}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {reduxLoading ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <RefreshCw className="mr-2" size={16} />
                )}
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {reduxError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{reduxError}</div>
                <div className="mt-4">
                  <button
                    onClick={handleRefresh}
                    className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Players Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {reduxLoading ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Player
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Game & Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member Since
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array(itemsPerPage).fill(0).map((_, index) => (
                    <TableRowSkeleton key={index} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : paginatedPlayers.length === 0 && !isFiltering ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No players found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding a new player'}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    clearDateFilter();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <UserPlus className="mr-2" size={16} />
                  Add Player
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader
                        sortKey="player_name"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Player
                      </SortableHeader>
                      <SortableHeader
                        sortKey="email"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Contact
                      </SortableHeader>
                      <SortableHeader
                        sortKey="game_name"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Game & Location
                      </SortableHeader>
                      <SortableHeader
                        sortKey="closed_date"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Status
                      </SortableHeader>
                      <SortableHeader
                        sortKey="member_since"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Member Since
                      </SortableHeader>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedPlayers
                      .filter(
                        (player) => {
                          // Filter out invalid player names
                          if (!player.player_name) return false;
                          const trimmedName = player.player_name.trim();
                          // Filter out entries that are just commas, empty, or invalid
                          if (!trimmedName || trimmedName === ',' || trimmedName.length < 2) return false;
                          return true;
                        }
                      )
                      // Remove duplicates based on player_number and player_name combination
                      .filter((player, index, array) => {
                        // Create a unique key for each player
                        const playerKey = `${player.player_number || 'no-number'}-${player.player_name?.trim() || 'no-name'}`;
                        
                        // Find the first occurrence of this player
                        const firstIndex = array.findIndex(p => {
                          const pKey = `${p.player_number || 'no-number'}-${p.player_name?.trim() || 'no-name'}`;
                          return pKey === playerKey;
                        });

                        // Only keep if this is the first occurrence
                        return firstIndex === index;
                      })
                      .sort((a, b) => {
                        // Place players with no open_date (Closed) first, then with open_date (Active)
                        if (!a.open_date && b.open_date) return -1;
                        if (a.open_date && !b.open_date) return 1;
                        return 0;
                      })
                      .map((player) => (
                        <tr
                          key={`${player.id}-${player.player_number || 'no-number'}-${player.player_name}`} // More unique key
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleViewPlayer(player)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                {player.image_url ? (
                                  <img
                                    src={player.image_url}
                                    alt={player.player_name}
                                    className="h-full w-full object-contain rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedPlayerImage(player.image_url);
                                      setSelectedPlayerName(player.player_name);
                                      setShowPlayerImageModal(true);
                                    }}
                                  />
                                ) : (
                                  <span className="text-sm font-medium text-blue-600">
                                    {getInitials(player.player_name)}
                                  </span>
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-blue-700">{player.player_name || 'N/A'}</div>
                                <div className="text-sm text-gray-500">{player.player_number || 'N/A'}</div>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{player.email || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{player.phone || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{player.game_name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{player.location || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(player.open_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(player?.member_since)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewPlayer(player);
                                }}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50 transition-colors"
                                title="View Details"
                              >
                                <Eye size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{displayStart}</span> to{" "}
                        <span className="font-medium">{displayEnd}</span> of{" "}
                        <span className="font-medium">{totalPlayers}</span> results
                      </p>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                        <option value={500}>500 per page</option>
                        <option value={1000}>1000 per page</option>
                        <option value={2000}>2000 per page</option>
                        <option value={3000}>3000 per page</option>
                      </select>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="First Page"
                        >
                          <span className="sr-only">First</span>
                          <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Previous Page"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        </button>

                        <div className="flex items-center space-x-1 px-2">
                          {renderPaginationButtons()}
                        </div>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Next Page"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Last Page"
                        >
                          <span className="sr-only">Last</span>
                          <ChevronsRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Player Details Modal */}
      {selectedPlayer && (
        <PlayerEntriesModal
          player={selectedPlayer}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPlayer(null);
          }}
        />
      )}

      {/* Date Range Picker Modal */}
      {showDatePicker && (
        <DateRangePicker
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange?.startDate}
          initialEndDate={dateRange?.endDate}
        />
      )}

      {showPlayerImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{selectedPlayerName}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowPlayerImageModal(false)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4">
              <img
                src={selectedPlayerImage}
                alt={selectedPlayerName}
                className="w-full h-auto max-h-[70vh] object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk Messaging Modal */}
      {showBulkMessagingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">SMS Messaging</h2>
                  <p className="text-sm text-gray-500">Send SMS to multiple players</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowBulkMessagingModal(false);
                  setIsBulkMessageEditable(false);
                  setSelectedPlayers(new Set());
                  setManualPhoneNumbers('');
                  setManualPhoneInput('');
                  setPhoneInputError('');
                  setBulkPhoneInput('');
                  setBulkPhoneError('');
                  setShowManualEntry(false);
                  setBulkModalSearchTerm('');
                  setDebouncedBulkSearchTerm('');
                  setBulkModalCurrentPage(1);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Message Textarea */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Message:
                  </label>
                  {!isBulkMessageEditable ? (
                    <button
                      onClick={() => setIsBulkMessageEditable(true)}
                      className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          const res = await playerProfileService.saveSmsTemplate(bulkMessageText, 'default');
                          if (res?.success) {
                            setIsBulkMessageEditable(false);
                            toast.success('Message saved to database!');
                          } else {
                            toast.error(res?.error || 'Failed to save template');
                          }
                        } catch (e) {
                          toast.error(e?.message || 'Failed to save template');
                        }
                      }}
                      className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                    >
                      <Check size={14} />
                      Save
                    </button>
                  )}
                </div>
                <textarea
                  value={bulkMessageText}
                  onChange={(e) => setBulkMessageText(e.target.value)}
                  placeholder="Enter your message to send to selected players..."
                  rows={12}
                  readOnly={!isBulkMessageEditable}
                  className={`w-full min-h-[280px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${
                    !isBulkMessageEditable ? 'bg-gray-50 cursor-default' : 'bg-white'
                  }`}
                />
                {!isBulkMessageEditable && (
                  <p className="text-xs text-gray-500 italic">
                    Click "Edit" button to customize this message
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {bulkMessageText.length} characters
                  </span>
                  <span className="text-xs text-gray-500">
                    {selectedPlayers.size} player(s) selected
                    {manualPhoneNumbersList.length > 0 && ` • ${manualPhoneNumbersList.length} manual number(s)`}
                  </span>
                </div>
              </div>

              {/* Send to Active Players Button */}
              <div className="space-y-2">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="block text-sm font-medium text-green-800">
                        Send to Active Players
                      </label>
                      <p className="text-xs text-green-600 mt-1">
                        Send this message to all active players
                      </p>
                    </div>
                    <button
                      onClick={handleSendToActivePlayers}
                      disabled={sendingToActivePlayers || !bulkMessageText.trim()}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {sendingToActivePlayers ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Send to Active Players
                        </>
                      )}
                    </button>
                  </div>
                  {stats && (
                    <p className="text-xs text-green-600 mt-2">
                      Currently {stats.active_profiles || 0} active players
                    </p>
                  )}
                </div>
              </div>

              {/* Manual Phone Number Entry */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Or Enter Phone Numbers Manually (USA):
                  </label>
                  <button
                    onClick={() => {
                      setShowManualEntry(!showManualEntry);
                      if (showManualEntry) {
                        setManualPhoneNumbers('');
                        setManualPhoneInput('');
                        setPhoneInputError('');
                        setBulkPhoneInput('');
                        setBulkPhoneError('');
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {showManualEntry ? 'Hide' : 'Show'}
                  </button>
                </div>
                
                {showManualEntry && (
                  <div className="space-y-3">
                    {/* Phone Number Input */}
                    <div className="space-y-1">
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="tel"
                            value={manualPhoneInput}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              // If user is deleting and value is just "+1" or "+", clear it
                              if (newValue === '+1' || newValue === '+') {
                                setManualPhoneInput('');
                                setPhoneInputError('');
                                return;
                              }
                              const formatted = formatPhoneInput(newValue);
                              setManualPhoneInput(formatted);
                              setPhoneInputError('');
                            }}
                            onKeyDown={(e) => {
                              // Allow backspace to delete everything including +1
                              if (e.key === 'Backspace' && manualPhoneInput === '+1') {
                                setManualPhoneInput('');
                                setPhoneInputError('');
                                e.preventDefault();
                                return;
                              }
                              // Handle Enter key
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddManualPhone();
                              }
                            }}
                            placeholder="+1XXXXXXXXXX or XXXXXXXXXX"
                            className={`w-full px-3 py-2 pr-8 border rounded-md focus:outline-none focus:ring-2 text-sm ${
                              phoneInputError 
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                          />
                          {manualPhoneInput && (
                            <button
                              onClick={() => {
                                setManualPhoneInput('');
                                setPhoneInputError('');
                              }}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                              type="button"
                            >
                              <X size={16} />
                            </button>
                          )}
                          {phoneInputError && (
                            <p className="text-xs text-red-600 mt-1">{phoneInputError}</p>
                          )}
                        </div>
                        <button
                          onClick={handleAddManualPhone}
                          disabled={!manualPhoneInput.trim()}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          Add
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Enter USA phone number (10 digits). Press Enter or click Add to add to list.
                      </p>
                    </div>

                    {/* Bulk Phone Number Entry */}
                    <div className="space-y-2 border-t pt-3 mt-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Or Paste Multiple Phone Numbers (Bulk Entry):
                      </label>
                      <div className="space-y-2">
                        <textarea
                          value={bulkPhoneInput}
                          onChange={(e) => {
                            setBulkPhoneInput(e.target.value);
                            setBulkPhoneError('');
                          }}
                          placeholder="Paste multiple phone numbers here (one per line, or separated by commas, spaces, etc.)&#10;Example:&#10;9564676622&#10;+19564676623&#10;9564676624, 9564676625"
                          rows={4}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm resize-none ${
                            bulkPhoneError 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-blue-500'
                          }`}
                        />
                        {bulkPhoneError && (
                          <p className="text-xs text-red-600">{bulkPhoneError}</p>
                        )}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            Numbers will be automatically validated and +1 prefix will be added if missing.
                          </p>
                          <button
                            onClick={handleBulkPhoneEntry}
                            disabled={!bulkPhoneInput.trim()}
                            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            Add All Valid Numbers
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Added Phone Numbers List */}
                    {manualPhoneNumbersList.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-gray-700">
                            Added Numbers ({manualPhoneNumbersList.length}):
                          </p>
                          <button
                            onClick={() => {
                              setManualPhoneNumbers('');
                              setManualPhoneInput('');
                              setPhoneInputError('');
                              setBulkPhoneInput('');
                              setBulkPhoneError('');
                            }}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Clear All
                          </button>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-2 max-h-32 overflow-y-auto">
                          <div className="flex flex-wrap gap-2">
                            {manualPhoneNumbersList.map((phone, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-blue-200 text-blue-700 text-xs"
                              >
                                <span>{phone}</span>
                                <button
                                  onClick={() => {
                                    const numbers = manualPhoneNumbers.split(/[,\n;]/).map(n => n.trim()).filter(n => n);
                                    const updated = numbers.filter((_, i) => i !== idx).join('\n');
                                    setManualPhoneNumbers(updated);
                                  }}
                                  className="text-red-500 hover:text-red-700 ml-1"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Player Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Players (with phone numbers):
                  </label>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    disabled={paginatedPlayersForModal.length === 0}
                  >
                    {paginatedPlayersForModal.length > 0 && 
                     paginatedPlayersForModal.every(p => selectedPlayers.has(getPlayerId(p)))
                      ? 'Deselect Page' 
                      : `Select Page (${paginatedPlayersForModal.length})`
                    }
                  </button>
                </div>

                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search players by name, number, or phone..."
                    value={bulkModalSearchTerm}
                    onChange={(e) => setBulkModalSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {bulkModalSearchTerm && (
                    <button
                      onClick={() => setBulkModalSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* Players List - Only render visible items for performance */}
                <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                  {playersWithPhone.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <p>
                        {bulkModalSearchTerm 
                          ? `No players found matching "${bulkModalSearchTerm}"`
                          : 'No players with phone numbers available'
                        }
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="divide-y divide-gray-200">
                        {paginatedPlayersForModal.map((player) => {
                          const playerId = getPlayerId(player);
                          const isSelected = selectedPlayers.has(playerId);
                          return (
                            <div
                              key={playerId}
                              className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                              onClick={() => handleTogglePlayerSelection(playerId)}
                            >
                              <div className="flex-shrink-0 mr-3">
                                {isSelected ? (
                                  <CheckSquare className="h-5 w-5 text-blue-600" />
                                ) : (
                                  <Square className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                  {player.player_name || 'N/A'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {player.phone} • {player.player_number || 'N/A'}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Pagination for modal */}
                      {totalModalPages > 1 && (
                        <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between bg-gray-50">
                          <div className="text-xs text-gray-600">
                            Showing {((bulkModalCurrentPage - 1) * bulkModalItemsPerPage) + 1} to{' '}
                            {Math.min(bulkModalCurrentPage * bulkModalItemsPerPage, playersWithPhone.length)} of{' '}
                            {playersWithPhone.length} players
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setBulkModalCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={bulkModalCurrentPage === 1}
                              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="text-xs text-gray-600">
                              Page {bulkModalCurrentPage} of {totalModalPages}
                            </span>
                            <button
                              onClick={() => setBulkModalCurrentPage(prev => Math.min(totalModalPages, prev + 1))}
                              disabled={bulkModalCurrentPage === totalModalPages}
                              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {playersWithPhone.length > 0 && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      {playersWithPhone.length} player(s) {debouncedBulkSearchTerm && `matching "${debouncedBulkSearchTerm}"`}
                    </p>
                    {playersWithPhone.length > bulkModalItemsPerPage && (
                      <select
                        value={bulkModalItemsPerPage}
                        onChange={(e) => {
                          setBulkModalItemsPerPage(Number(e.target.value));
                          setBulkModalCurrentPage(1);
                        }}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                        <option value={200}>200 per page</option>
                      </select>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {selectedPlayers.size > 0 || manualPhoneNumbersList.length > 0 ? (
                  <span>
                    {selectedPlayers.size + manualPhoneNumbersList.length} recipient(s) selected • Ready to send
                  </span>
                ) : (
                  <span className="text-gray-400">Select players or enter phone numbers to send message</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowBulkMessagingModal(false);
                    setIsBulkMessageEditable(false);
                    setSelectedPlayers(new Set());
                    setManualPhoneNumbers('');
                    setManualPhoneInput('');
                    setPhoneInputError('');
                    setBulkPhoneInput('');
                    setBulkPhoneError('');
                    setShowManualEntry(false);
                    setBulkModalSearchTerm('');
                    setDebouncedBulkSearchTerm('');
                    setBulkModalCurrentPage(1);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkSendMessages}
                  disabled={sendingBulkMessages || !bulkMessageText.trim() || (selectedPlayers.size === 0 && manualPhoneNumbersList.length === 0)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {sendingBulkMessages ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send to {selectedPlayers.size + manualPhoneNumbersList.length} Recipient(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showSendConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Confirm SMS Send</h2>
                  <p className="text-sm text-gray-500">Review selected players before sending</p>
                </div>
              </div>
              <button
                onClick={() => setShowSendConfirmationModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Message Preview */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message to Send:
                </label>
                <div className="bg-white border border-gray-300 rounded-md p-3 text-sm text-gray-900 whitespace-pre-wrap">
                  {bulkMessageText}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {bulkMessageText.length} characters
                </p>
              </div>

              {/* Selected Players List */}
              <div className="space-y-4">
                {/* Selected Players from Database */}
                {selectedPlayerNames.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Selected Players ({selectedPlayerNames.length}):
                      </label>
                    </div>
                    <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                      <div className="divide-y divide-gray-200">
                        {selectedPlayerNames.map((player, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {player.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {player.phone} • Player #{player.playerNumber}
                              </div>
                            </div>
                            <div className="ml-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ Ready
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Manual Phone Numbers */}
                {manualPhoneNumbersList.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Manual Phone Numbers ({manualPhoneNumbersList.length}):
                      </label>
                    </div>
                    <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                      <div className="divide-y divide-gray-200">
                        {manualPhoneNumbersList.map((phone, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {phone}
                              </div>
                              <div className="text-xs text-gray-500">
                                Manual entry
                              </div>
                            </div>
                            <div className="ml-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                ✓ Ready
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {selectedPlayerNames.length === 0 && manualPhoneNumbersList.length === 0 && (
                  <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
                    <p>No recipients selected</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span>
                  Ready to send SMS to {selectedPlayerNames.length + manualPhoneNumbersList.length} recipient(s)
                  {selectedPlayerNames.length > 0 && ` (${selectedPlayerNames.length} players`}
                  {selectedPlayerNames.length > 0 && manualPhoneNumbersList.length > 0 && `, `}
                  {manualPhoneNumbersList.length > 0 && `${manualPhoneNumbersList.length} manual`}
                  {selectedPlayerNames.length > 0 && `)`}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowSendConfirmationModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAndSendMessages}
                  disabled={sendingBulkMessages}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {sendingBulkMessages ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Confirm & Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Players Send Confirmation Modal */}
      {showActivePlayersSendConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Send className="h-5 w-5 text-green-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Confirm Send to Active Players</h2>
                  <p className="text-sm text-gray-500">This will send the message to all active players</p>
                </div>
              </div>
              <button
                onClick={() => setShowActivePlayersSendConfirmationModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium">
                  Recipients: approximately {stats?.active_profiles || 0} active player(s)
                </p>
                <p className="text-xs text-green-700 mt-1">
                  We will fetch active players with valid phone numbers, then send the SMS in bulk.
                </p>
              </div>

              {/* Message Preview */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message to Send:
                </label>
                <div className="bg-white border border-gray-300 rounded-md p-3 text-sm text-gray-900 whitespace-pre-wrap">
                  {bulkMessageText}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {bulkMessageText.length} characters
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end items-center gap-3">
              <button
                onClick={() => setShowActivePlayersSendConfirmationModal(false)}
                disabled={sendingToActivePlayers}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndSendToActivePlayers}
                disabled={sendingToActivePlayers || !bulkMessageText.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {sendingToActivePlayers ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Yes, Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Players Send Result Modal – counts and SMS logs for send date */}
      {showActiveSendResultModal && activeSendResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Send result</h2>
              <button
                onClick={() => {
                  setShowActiveSendResultModal(false);
                  setActiveSendResult(null);
                  setActiveSendResultLogsSummary(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-xs font-medium text-green-700 uppercase tracking-wider">Sent successfully</p>
                  <p className="text-2xl font-bold text-green-800">{activeSendResult.successCount}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">Skipped</p>
                  <p className="text-2xl font-bold text-amber-800">{activeSendResult.skipCount || 0}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-xs font-medium text-red-700 uppercase tracking-wider">Failed</p>
                  <p className="text-2xl font-bold text-red-800">{activeSendResult.failCount}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Total active players attempted: <span className="font-semibold">{activeSendResult.total}</span>
              </p>

              {activeSendResult.skipped?.length > 0 && (
                <div className="border border-amber-200 rounded-lg overflow-hidden">
                  <p className="px-3 py-2 bg-amber-50 text-sm font-medium text-amber-800 border-b border-amber-200">
                    Invalid or unsubscribed numbers ({activeSendResult.skipped.length})
                  </p>
                  <ul className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                    {activeSendResult.skipped.map((f, i) => (
                      <li key={`skip-${i}`} className="px-3 py-2 text-sm">
                        <span className="font-medium text-gray-800">{f.phone || '—'}</span>
                        {f.error && <span className="block text-xs text-amber-700 mt-0.5">{f.error}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {activeSendResult.failed && activeSendResult.failed.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <p className="px-3 py-2 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-200">
                    Failed numbers ({activeSendResult.failed.length})
                  </p>
                  <ul className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                    {activeSendResult.failed.map((f, i) => (
                      <li key={i} className="px-3 py-2 text-sm">
                        <span className="font-medium text-gray-800">{f.phone || '—'}</span>
                        {f.error && <span className="block text-xs text-red-600 mt-0.5">{f.error}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="pt-2 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">SMS logs for this date (re-derived from logs)</p>
                {activeSendResultLogsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading…
                  </div>
                ) : activeSendResultLogsSummary?.error ? (
                  <p className="text-sm text-red-600">Could not load logs for this date.</p>
                ) : activeSendResultLogsSummary ? (
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="text-green-700">
                      Sent: <strong>{activeSendResultLogsSummary.sent}</strong>
                    </span>
                    <span className="text-red-700">
                      Failed: <strong>{activeSendResultLogsSummary.failed}</strong>
                    </span>
                    <span className="text-gray-600">
                      Total on date: <strong>{activeSendResultLogsSummary.total}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={fetchActiveSendResultLogsSummary}
                      className="text-blue-600 hover:underline"
                    >
                      Refresh count
                    </button>
                  </div>
                ) : null}
                <p className="text-xs text-gray-500 mt-1">
                  {activeSendResult.sendDate && new Date(activeSendResult.sendDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </p>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setShowActiveSendResultModal(false);
                  setActiveSendResult(null);
                  setActiveSendResultLogsSummary(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerProfileModule;